import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './JourneyBuilderScreen.scss';

// =============================================================================
// RECRUITMENT-SPECIFIC TYPES & INTERFACES
// =============================================================================

export interface FlowNode {
  id: string;
  type: string;
  title: string;
  description: string;
  position: { x: number; y: number };
  config: Record<string, any>;
  connections: string[];
  category?: string;
}

export interface NodeType {
  type: string;
  icon: string;
  title: string;
  description: string;
  color: string;
  examples?: string[];
}

// =============================================================================
// HR RECRUITMENT NODE TYPES
// =============================================================================

export const HR_NODE_TYPES: NodeType[] = [
  {
    type: 'trigger',
    icon: '🚀',
    title: 'Trigger',
    description: 'Start the candidate journey',
    color: '#10B981',
    examples: ['Application received', 'Profile updated', 'Interview scheduled']
  },
  {
    type: 'email',
    icon: '📧',
    title: 'Email',
    description: 'Send automated email',
    color: '#3B82F6',
    examples: ['Welcome email', 'Interview invitation', 'Status update']
  },
  {
    type: 'sms',
    icon: '📱',
    title: 'SMS',
    description: 'Send SMS notification',
    color: '#8B5CF6',
    examples: ['Interview reminder', 'Application confirmation']
  },
  {
    type: 'whatsapp',
    icon: '💚',
    title: 'WhatsApp',
    description: 'Send WhatsApp message',
    color: '#25D366',
    examples: ['Rich messages', 'Media attachments', 'Interactive buttons']
  },
  {
    type: 'delay',
    icon: '⏰',
    title: 'Wait',
    description: 'Add delay/waiting period',
    color: '#F59E0B',
    examples: ['Wait 24 hours', 'Wait for response', 'Delay until date']
  },
  {
    type: 'condition',
    icon: '🔀',
    title: 'Decision',
    description: 'Branch based on conditions',
    color: '#EF4444',
    examples: ['Check application status', 'Evaluate score', 'Verify requirements']
  },
  {
    type: 'action',
    icon: '⚡',
    title: 'Action',
    description: 'Trigger system action',
    color: '#F97316',
    examples: ['Update status', 'Schedule interview', 'Create task']
  },
  {
    type: 'webhook',
    icon: '🔗',
    title: 'Webhook',
    description: 'Call external system',
    color: '#6B7280',
    examples: ['ATS integration', 'Background check', 'Reference check']
  },
  {
    type: 'segment',
    icon: '🎯',
    title: 'Segment',
    description: 'Filter candidates',
    color: '#EC4899',
    examples: ['Filter by skills', 'Location filter', 'Experience level']
  }
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

const findNonOverlappingPosition = (
  desiredPosition: { x: number; y: number },
  existingNodes: FlowNode[],
  nodeWidth: number,
  nodeHeight: number,
  minSpacing: number
): { x: number; y: number } => {
  const { x, y } = desiredPosition;
  
  const isOverlapping = (testX: number, testY: number) => {
    return existingNodes.some(node => {
      const nodeX = node.position.x;
      const nodeY = node.position.y;
      
      return (
        testX < nodeX + nodeWidth + minSpacing &&
        testX + nodeWidth + minSpacing > nodeX &&
        testY < nodeY + nodeHeight + minSpacing &&
        testY + nodeHeight + minSpacing > nodeY
      );
    });
  };
  
  if (!isOverlapping(x, y)) {
    return { x, y };
  }
  
  const searchRadius = 150;
  const step = 30;
  
  for (let radius = step; radius <= searchRadius; radius += step) {
    for (let angle = 0; angle < 360; angle += 45) {
      const rad = (angle * Math.PI) / 180;
      const testX = x + Math.cos(rad) * radius;
      const testY = y + Math.sin(rad) * radius;
      
      if (testX >= 0 && testY >= 0 && !isOverlapping(testX, testY)) {
        return { x: testX, y: testY };
      }
    }
  }
  
  const gridX = Math.floor(existingNodes.length / 4) * (nodeWidth + minSpacing);
  const gridY = (existingNodes.length % 4) * (nodeHeight + minSpacing);
  
  return { x: gridX, y: gridY };
};

// =============================================================================
// MAIN JOURNEY BUILDER SCREEN COMPONENT
// =============================================================================

const JourneyBuilderScreen = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [journeyName, setJourneyName] = useState('New Candidate Journey');
  const [journeyDescription, setJourneyDescription] = useState('');
  const [selectedTrigger, setSelectedTrigger] = useState('application_received');
  const [selectedNode, setSelectedNode] = useState(null);
  const [draggedNodeType, setDraggedNodeType] = useState(null);
  const [draggedNode, setDraggedNode] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragPreview, setDragPreview] = useState(null);
  const [showNodeConfig, setShowNodeConfig] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [nodes, setNodes] = useState([
    {
      id: 'trigger-start',
      type: 'trigger',
      title: 'Journey Start',
      description: 'Candidate journey begins',
      position: { x: 100, y: 100 },
      config: { 
        trigger: 'application_received',
        description: 'Automatically triggered when condition is met'
      },
      connections: []
    }
  ]);

  // Trigger options for HR recruitment
  const triggerOptions = [
    { value: 'application_received', label: 'Application Received' },
    { value: 'profile_completed', label: 'Profile Completed' },
    { value: 'interview_scheduled', label: 'Interview Scheduled' },
    { value: 'interview_completed', label: 'Interview Completed' },
    { value: 'offer_extended', label: 'Offer Extended' },
    { value: 'offer_accepted', label: 'Offer Accepted' },
    { value: 'manual_trigger', label: 'Manual Trigger' }
  ];

  // Load existing journey if editing
  useEffect(() => {
    if (isEditing) {
      // Load journey data from localStorage or API
      const savedJourneys = JSON.parse(localStorage.getItem('hr_journeys') || '[]');
      const journey = savedJourneys.find(j => j.id === id);
      
      if (journey) {
        setJourneyName(journey.name);
        setJourneyDescription(journey.description || '');
        setSelectedTrigger(journey.trigger || 'application_received');
        setNodes(journey.nodes || []);
      }
    }
  }, [id, isEditing]);

  // Handle adding new nodes to canvas
  const handleAddNode = useCallback((type: string, position: { x: number; y: number }) => {
    const nodeType = HR_NODE_TYPES.find(nt => nt.type === type);
    
    const newNode: FlowNode = {
      id: `${type}-${Date.now()}`,
      type,
      title: `${nodeType?.title || 'New'} Step`,
      description: 'Configure this step',
      position,
      config: getDefaultConfig(type),
      connections: []
    };
    
    setNodes(prev => [...prev, newNode]);
  }, []);

  // Get default configuration for node types
  const getDefaultConfig = (nodeType: string) => {
    const configs = {
      email: {
        subject: '',
        template: '',
        delay: { amount: 0, unit: 'hours' }
      },
      sms: {
        message: '',
        delay: { amount: 0, unit: 'hours' }
      },
      delay: {
        duration: { amount: 1, unit: 'days' }
      },
      condition: {
        field: '',
        operator: '',
        value: '',
        trueAction: '',
        falseAction: ''
      },
      action: {
        actionType: '',
        parameters: {}
      },
      webhook: {
        url: '',
        method: 'POST',
        headers: {},
        payload: ''
      },
      segment: {
        criteria: [],
        operator: 'and'
      }
    };
    
    return configs[nodeType] || {};
  };

  // Handle updating nodes
  const handleUpdateNode = useCallback((nodeId: string, updates: Partial<FlowNode>) => {
    setNodes(prev => prev.map(node =>
      node.id === nodeId ? { ...node, ...updates } : node
    ));
    
    if (selectedNode?.id === nodeId) {
      setSelectedNode(prev => prev ? { ...prev, ...updates } : null);
    }
  }, [selectedNode]);

  // Handle deleting nodes
  const handleDeleteNode = useCallback((nodeId: string) => {
    const nodeToDelete = nodes.find(node => node.id === nodeId);
    if (nodeToDelete && nodeToDelete.id !== 'trigger-start') {
      if (window.confirm(`Delete "${nodeToDelete.title}"?`)) {
        setNodes(prev => prev.filter(node => node.id !== nodeId));
        
        // Remove connections to this node
        setNodes(prev => prev.map(node => ({
          ...node,
          connections: node.connections.filter(id => id !== nodeId)
        })));
        
        if (selectedNode?.id === nodeId) {
          setSelectedNode(null);
        }
      }
    }
  }, [nodes, selectedNode]);

  // Handle connecting nodes
  const handleConnectNodes = useCallback((fromNodeId: string, toNodeId: string) => {
    setNodes(prev => prev.map(node =>
      node.id === fromNodeId && !node.connections.includes(toNodeId)
        ? { ...node, connections: [...node.connections, toNodeId] }
        : node
    ));
  }, []);

  // Handle disconnecting nodes
  const handleDisconnectNodes = useCallback((fromNodeId: string, toNodeId: string) => {
    setNodes(prev => prev.map(node =>
      node.id === fromNodeId
        ? { ...node, connections: node.connections.filter(id => id !== toNodeId) }
        : node
    ));
  }, []);

  // Handle drag start from palette
  const handleDragStart = (e, nodeType) => {
    setDraggedNodeType(nodeType);
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Handle node drag start
  const handleNodeDragStart = (e, node) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const canvasRect = e.currentTarget.closest('.canvas-container')?.getBoundingClientRect();
    
    if (canvasRect) {
      setDragOffset({
        x: e.clientX - (canvasRect.left + node.position.x),
        y: e.clientY - (canvasRect.top + node.position.y)
      });
    }
    
    setDraggedNode(node.id);
    setSelectedNode(node);
  };

  // Handle node drag
  const handleNodeDrag = (e) => {
    if (!draggedNode) return;
    
    const canvasContainer = document.querySelector('.canvas-container');
    if (!canvasContainer) return;
    
    const rect = canvasContainer.getBoundingClientRect();
    const scrollLeft = canvasContainer.scrollLeft;
    const scrollTop = canvasContainer.scrollTop;
    
    let x = e.clientX - rect.left - dragOffset.x + scrollLeft;
    let y = e.clientY - rect.top - dragOffset.y + scrollTop;
    
    // Snap to grid
    const gridSize = 20;
    x = Math.round(x / gridSize) * gridSize;
    y = Math.round(y / gridSize) * gridSize;
    
    // Ensure minimum bounds
    x = Math.max(0, x);
    y = Math.max(0, y);
    
    handleUpdateNode(draggedNode, { position: { x, y } });
  };

  // Handle node drag end
  const handleNodeDragEnd = () => {
    setDraggedNode(null);
    setDragOffset({ x: 0, y: 0 });
  };

  // Handle canvas drop
  const handleCanvasDrop = (e) => {
    e.preventDefault();
    if (draggedNodeType) {
      const rect = e.currentTarget.getBoundingClientRect();
      const scrollLeft = e.currentTarget.scrollLeft;
      const scrollTop = e.currentTarget.scrollTop;
      
      let x = e.clientX - rect.left + scrollLeft;
      let y = e.clientY - rect.top + scrollTop;
      
      const gridSize = 20;
      x = Math.round(x / gridSize) * gridSize;
      y = Math.round(y / gridSize) * gridSize;
      
      const minSpacing = 60;
      const nodeWidth = 200;
      const nodeHeight = 120;
      
      const adjustedPosition = findNonOverlappingPosition(
        { x, y }, 
        nodes, 
        nodeWidth, 
        nodeHeight, 
        minSpacing
      );
      
      handleAddNode(draggedNodeType, adjustedPosition);
      setDraggedNodeType(null);
      setDragPreview(null);
    }
  };

  // Handle canvas drag over
  const handleCanvasDragOver = (e) => {
    e.preventDefault();
    if (draggedNodeType) {
      const rect = e.currentTarget.getBoundingClientRect();
      const scrollLeft = e.currentTarget.scrollLeft;
      const scrollTop = e.currentTarget.scrollTop;
      
      let x = e.clientX - rect.left + scrollLeft;
      let y = e.clientY - rect.top + scrollTop;
      
      const gridSize = 20;
      x = Math.round(x / gridSize) * gridSize;
      y = Math.round(y / gridSize) * gridSize;
      
      setDragPreview({ x, y });
    }
  };

  // Handle save journey
  const handleSave = async () => {
    if (!journeyName.trim()) {
      alert('Please enter a journey name');
      return;
    }

    setIsSaving(true);

    const journeyData = {
      id: id || Date.now().toString(),
      name: journeyName,
      description: journeyDescription,
      trigger: selectedTrigger,
      nodes: nodes,
      type: 'journey',
      status: 'draft',
      createdAt: isEditing ? undefined : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    try {
      // Save to localStorage (replace with API call)
      const savedJourneys = JSON.parse(localStorage.getItem('hr_journeys') || '[]');
      
      if (isEditing) {
        const index = savedJourneys.findIndex(j => j.id === id);
        if (index !== -1) {
          savedJourneys[index] = { ...savedJourneys[index], ...journeyData };
        } else {
          savedJourneys.push(journeyData);
        }
      } else {
        savedJourneys.push(journeyData);
      }
      
      localStorage.setItem('hr_journeys', JSON.stringify(savedJourneys));
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      alert('Journey saved successfully!');
      navigate('/campaigns');
    } catch (error) {
      alert('Error saving journey. Please try again.');
      console.error('Save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (window.confirm('Are you sure you want to go back? Any unsaved changes will be lost.')) {
      navigate('/campaigns');
    }
  };

  // Update trigger node when trigger changes
  useEffect(() => {
    const triggerNode = nodes.find(n => n.id === 'trigger-start');
    if (triggerNode) {
      handleUpdateNode('trigger-start', {
        config: { 
          ...triggerNode.config, 
          trigger: selectedTrigger,
          description: triggerOptions.find(t => t.value === selectedTrigger)?.label || selectedTrigger
        }
      });
    }
  }, [selectedTrigger]);

  // Calculate canvas dimensions
  const canvasWidth = Math.max(1200, Math.max(...nodes.map(n => n.position.x + 300), 0));
  const canvasHeight = Math.max(800, Math.max(...nodes.map(n => n.position.y + 200), 0));

  // Get node type info
  const getNodeType = (type: string) => {
    return HR_NODE_TYPES.find(nt => nt.type === type) || HR_NODE_TYPES[0];
  };

  // Hide body scroll when component mounts
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  return (
    <div className="journey-builder-screen">
      {/* Header */}
      <div className="screen-header">
        <div className="header-left">
          <button className="back-btn" onClick={handleBack}>
            ← Back to Campaigns
          </button>
          <div className="header-divider"></div>
          <div className="breadcrumb">
            <span>Journey Builder</span>
            {isEditing && (
              <>
                <span className="separator">/</span>
                <span>Edit Journey</span>
              </>
            )}
          </div>
        </div>

        <div className="header-center">
          <div className="journey-config">
            <input
              type="text"
              value={journeyName || 'New Candidate Journey'}
              onChange={(e) => setJourneyName(e.target.value)}
              className="journey-name-input"
              placeholder="New Journey"
            />
            <select
              value={selectedTrigger}
              onChange={(e) => setSelectedTrigger(e.target.value)}
              className="trigger-select"
            >
              {triggerOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="header-right">
          <button className="btn btn-secondary" onClick={handleBack}>
            Cancel
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : isEditing ? 'Update Journey' : 'Save Journey'}
          </button>
        </div>
      </div>

      {/* Journey Description */}
      <div className="journey-description-bar">
        <textarea
          value={journeyDescription}
          onChange={(e) => setJourneyDescription(e.target.value)}
          className="journey-description"
          placeholder="Describe what this journey accomplishes for candidate engagement..."
          rows="1"
        />
      </div>

      <div className="screen-content">
        {/* Node Palette */}
        <div className="node-palette">
          <div className="palette-header">
            <h3>🧩 Journey Components</h3>
            <p>Drag components to canvas</p>
          </div>
          
          <div className="palette-items">
            {HR_NODE_TYPES.filter(type => type.type !== 'trigger').map((nodeType) => (
              <div
                key={nodeType.type}
                className="palette-item"
                draggable
                onDragStart={(e) => handleDragStart(e, nodeType.type)}
                style={{ borderColor: nodeType.color }}
              >
                <div className="palette-icon" style={{ color: nodeType.color }}>
                  {nodeType.icon}
                </div>
                <div className="palette-info">
                  <div className="palette-name">{nodeType.title}</div>
                  <div className="palette-desc">{nodeType.description}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Journey Stats */}
          <div className="journey-stats">
            <h4>Journey Statistics</h4>
            <div className="stat-items">
              <div className="stat-item">
                <span className="stat-label">Total Steps:</span>
                <span className="stat-value">{nodes.length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Connections:</span>
                <span className="stat-value">
                  {nodes.reduce((acc, node) => acc + node.connections.length, 0)}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Trigger:</span>
                <span className="stat-value">
                  {triggerOptions.find(t => t.value === selectedTrigger)?.label}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Canvas Area */}
        <div className="canvas-container">
          <div
            className="journey-canvas"
            onDrop={handleCanvasDrop}
            onDragOver={handleCanvasDragOver}
            onDragLeave={() => setDragPreview(null)}
            onMouseMove={handleNodeDrag}
            onMouseUp={handleNodeDragEnd}
            onClick={() => setSelectedNode(null)}
            style={{ width: canvasWidth, height: canvasHeight }}
          >
            {/* Grid Background */}
            <div className="grid-background" />

            {/* Connection Lines */}
            <svg className="connection-overlay" width="100%" height="100%">
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon points="0 0, 10 3.5, 0 7" fill="#6B7280" />
                </marker>
              </defs>
              
              {nodes.map(node =>
                node.connections.map(connectionId => {
                  const targetNode = nodes.find(n => n.id === connectionId);
                  if (!targetNode) return null;
                  
                  const startX = node.position.x + 100;
                  const startY = node.position.y + 60;
                  const endX = targetNode.position.x + 100;
                  const endY = targetNode.position.y;
                  
                  // Calculate curved path
                  const midY = startY + (endY - startY) / 2;
                  const path = `M ${startX} ${startY} Q ${startX} ${midY} ${(startX + endX) / 2} ${midY} Q ${endX} ${midY} ${endX} ${endY}`;
                  
                  return (
                    <path
                      key={`connection-${node.id}-${connectionId}`}
                      d={path}
                      stroke="#6B7280"
                      strokeWidth="2"
                      fill="none"
                      markerEnd="url(#arrowhead)"
                      className="connection-line"
                    />
                  );
                })
              )}
            </svg>

            {/* Journey Nodes */}
            {nodes.map((node) => {
              const nodeType = getNodeType(node.type);
              const isSelected = selectedNode?.id === node.id;
              const isDragging = draggedNode === node.id;

              return (
                <div
                  key={node.id}
                  className={`journey-node ${node.type} ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}
                  style={{
                    left: node.position.x,
                    top: node.position.y,
                    borderColor: nodeType.color
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedNode(node);
                  }}
                  onDoubleClick={() => {
                    setSelectedNode(node);
                    setShowNodeConfig(true);
                  }}
                  onMouseDown={(e) => handleNodeDragStart(e, node)}
                >
                  {/* Node Header */}
                  <div className="node-header">
                    <div className="node-icon" style={{ color: nodeType.color }}>
                      {nodeType.icon}
                    </div>
                    <div className="node-title">{node.title}</div>
                    
                    {isSelected && node.id !== 'trigger-start' && (
                      <div className="node-actions">
                        <button
                          className="action-btn edit-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowNodeConfig(true);
                          }}
                          title="Configure step"
                        >
                          ⚙️
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNode(node.id);
                          }}
                          title="Delete step"
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Node Content */}
                  <div className="node-content">
                    <div className="node-description">{node.description}</div>
                    
                    {/* Configuration Preview */}
                    {node.config && Object.keys(node.config).length > 0 && (
                      <div className="config-preview">
                        {node.type === 'email' && node.config.subject && (
                          <div className="config-item">
                            📧 {node.config.subject}
                          </div>
                        )}
                        {node.type === 'sms' && node.config.message && (
                          <div className="config-item">
                            📱 {node.config.message.substring(0, 30)}...
                          </div>
                        )}
                        {node.type === 'whatsapp' && node.config.message && (
                          <div className="config-item">
                            💚 {node.config.message.substring(0, 30)}...
                          </div>
                        )}
                        {node.type === 'delay' && node.config.duration && (
                          <div className="config-item">
                            ⏰ Wait {node.config.duration.amount} {node.config.duration.unit}
                          </div>
                        )}
                        {node.type === 'condition' && node.config.field && (
                          <div className="config-item">
                            🔀 If {node.config.field}...
                          </div>
                        )}
                        {node.type === 'action' && node.config.actionType && (
                          <div className="config-item">
                            ⚡ {node.config.actionType}
                          </div>
                        )}
                        {node.type === 'trigger' && (
                          <div className="config-item">
                            🚀 {node.config.description}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Drag Preview */}
            {dragPreview && draggedNodeType && (
              <div
                className="drag-preview"
                style={{
                  left: dragPreview.x,
                  top: dragPreview.y
                }}
              >
                <div className="preview-icon">
                  {getNodeType(draggedNodeType).icon}
                </div>
                <div className="preview-text">
                  Drop to add {getNodeType(draggedNodeType).title}
                </div>
              </div>
            )}

            {/* Empty State */}
            {nodes.length === 1 && (
              <div className="empty-state">
                <div className="empty-icon">🎯</div>
                <h3>Build Your Candidate Journey</h3>
                <p>Drag components from the left panel to create an automated recruitment workflow</p>
              </div>
            )}
          </div>
        </div>

        {/* Properties Panel */}
        <div className="properties-panel">
          <div className="panel-header">
            <h3>⚙️ Properties</h3>
          </div>
          
          {selectedNode ? (
            <div className="node-properties">
              <h4>{selectedNode.title}</h4>
              
              <div className="property-group">
                <label>Step Name:</label>
                <input
                  type="text"
                  value={selectedNode.title}
                  onChange={(e) => handleUpdateNode(selectedNode.id, { title: e.target.value })}
                  className="property-input"
                />
              </div>
              
              <div className="property-group">
                <label>Description:</label>
                <textarea
                  value={selectedNode.description}
                  onChange={(e) => handleUpdateNode(selectedNode.id, { description: e.target.value })}
                  className="property-textarea"
                  rows="3"
                />
              </div>

              {/* Connections */}
              <div className="property-group">
                <label>Connections:</label>
                <div className="connections-list">
                  {selectedNode.connections.length > 0 ? (
                    selectedNode.connections.map((connectionId) => {
                      const targetNode = nodes.find(n => n.id === connectionId);
                      return (
                        <div key={connectionId} className="connection-item">
                          <span>{targetNode?.title || 'Unknown'}</span>
                          <button
                            className="remove-connection"
                            onClick={() => handleDisconnectNodes(selectedNode.id, connectionId)}
                          >
                            ✕
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <p className="no-connections">No connections</p>
                  )}
                  
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleConnectNodes(selectedNode.id, e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="connect-select"
                  >
                    <option value="">Connect to...</option>
                    {nodes
                      .filter(node => node.id !== selectedNode.id && !selectedNode.connections.includes(node.id))
                      .map(node => (
                        <option key={node.id} value={node.id}>
                          {node.title}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              
              <button 
                className="btn btn-primary"
                onClick={() => setShowNodeConfig(true)}
              >
                Configure Step
              </button>
              
              {selectedNode.id !== 'trigger-start' && (
                <button 
                  className="btn btn-danger"
                  onClick={() => handleDeleteNode(selectedNode.id)}
                >
                  Delete Step
                </button>
              )}
            </div>
          ) : (
            <div className="no-selection">
              <div className="no-selection-icon">🎯</div>
              <p>Select a step to configure its properties</p>
            </div>
          )}
        </div>
      </div>

      {/* Step Configuration Modal */}
      {showNodeConfig && selectedNode && (
        <HRStepConfigModal
          node={selectedNode}
          onSave={(updatedNode) => {
            handleUpdateNode(updatedNode.id, updatedNode);
            setShowNodeConfig(false);
          }}
          onClose={() => setShowNodeConfig(false)}
        />
      )}
    </div>
  );
};

// =============================================================================
// HR STEP CONFIGURATION MODAL (Reused from previous implementation)
// =============================================================================

const HRStepConfigModal = ({ node, onSave, onClose }) => {
  const [nodeConfig, setNodeConfig] = useState({ ...node });

  const updateConfig = (key, value) => {
    setNodeConfig(prev => ({
      ...prev,
      config: {
        ...prev.config,
        [key]: value
      }
    }));
  };

  const handleSave = () => {
    onSave(nodeConfig);
  };

  return (
    <div className="step-config-modal-overlay">
      <div className="step-config-modal">
        <div className="modal-header">
          <h3>Configure {node.title}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">
          {/* Email Configuration */}
          {node.type === 'email' && (
            <>
              <div className="form-group">
                <label>Email Subject</label>
                <input
                  type="text"
                  value={nodeConfig.config?.subject || ''}
                  onChange={(e) => updateConfig('subject', e.target.value)}
                  placeholder="Enter email subject line"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Email Template</label>
                <textarea
                  value={nodeConfig.config?.template || ''}
                  onChange={(e) => updateConfig('template', e.target.value)}
                  placeholder="Enter email content with merge tags like {{candidate_name}}"
                  className="form-textarea"
                  rows="8"
                />
              </div>

              <div className="form-group">
                <label>Send Delay</label>
                <div className="delay-inputs">
                  <input
                    type="number"
                    value={nodeConfig.config?.delay?.amount || 0}
                    onChange={(e) => updateConfig('delay', {
                      ...nodeConfig.config?.delay,
                      amount: parseInt(e.target.value) || 0
                    })}
                    className="form-input"
                    min="0"
                  />
                  <select
                    value={nodeConfig.config?.delay?.unit || 'hours'}
                    onChange={(e) => updateConfig('delay', {
                      ...nodeConfig.config?.delay,
                      unit: e.target.value
                    })}
                    className="form-select"
                  >
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {/* SMS Configuration */}
          {node.type === 'sms' && (
            <>
              <div className="form-group">
                <label>SMS Message</label>
                <textarea
                  value={nodeConfig.config?.message || ''}
                  onChange={(e) => updateConfig('message', e.target.value)}
                  placeholder="Enter SMS message (160 characters max)"
                  className="form-textarea"
                  rows="4"
                  maxLength="160"
                />
                <small className="char-count">
                  {(nodeConfig.config?.message || '').length}/160 characters
                </small>
              </div>

              <div className="form-group">
                <label>Send Delay</label>
                <div className="delay-inputs">
                  <input
                    type="number"
                    value={nodeConfig.config?.delay?.amount || 0}
                    onChange={(e) => updateConfig('delay', {
                      ...nodeConfig.config?.delay,
                      amount: parseInt(e.target.value) || 0
                    })}
                    className="form-input"
                    min="0"
                  />
                  <select
                    value={nodeConfig.config?.delay?.unit || 'hours'}
                    onChange={(e) => updateConfig('delay', {
                      ...nodeConfig.config?.delay,
                      unit: e.target.value
                    })}
                    className="form-select"
                  >
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {/* WhatsApp Configuration */}
          {node.type === 'whatsapp' && (
            <>
              <div className="form-group">
                <label>WhatsApp Message</label>
                <textarea
                  value={nodeConfig.config?.message || ''}
                  onChange={(e) => updateConfig('message', e.target.value)}
                  placeholder="Enter WhatsApp message (4096 characters max) - You can use emojis! 😊"
                  className="form-textarea"
                  rows="6"
                  maxLength="4096"
                />
                <small className="char-count">
                  {(nodeConfig.config?.message || '').length}/4096 characters
                </small>
              </div>
              <div className="form-group">
                <label>Message Type</label>
                <select
                  value={nodeConfig.config?.mediaType || 'text'}
                  onChange={(e) => updateConfig('mediaType', e.target.value)}
                  className="form-select"
                >
                  <option value="text">Text Only</option>
                  <option value="image">Image</option>
                  <option value="document">Document</option>
                  <option value="video">Video</option>
                </select>
              </div>
              {nodeConfig.config?.mediaType && nodeConfig.config.mediaType !== 'text' && (
                <div className="form-group">
                  <label>Media URL</label>
                  <input
                    type="url"
                    value={nodeConfig.config?.mediaUrl || ''}
                    onChange={(e) => updateConfig('mediaUrl', e.target.value)}
                    placeholder="https://example.com/media.jpg"
                    className="form-input"
                  />
                </div>
              )}
              <div className="form-group">
                <label>Button Text (Optional)</label>
                <input
                  type="text"
                  value={nodeConfig.config?.buttonText || ''}
                  onChange={(e) => updateConfig('buttonText', e.target.value)}
                  placeholder="e.g., View Details, Apply Now"
                  className="form-input"
                />
              </div>
              {nodeConfig.config?.buttonText && (
                <div className="form-group">
                  <label>Button URL</label>
                  <input
                    type="url"
                    value={nodeConfig.config?.buttonUrl || ''}
                    onChange={(e) => updateConfig('buttonUrl', e.target.value)}
                    placeholder="https://example.com/action"
                    className="form-input"
                  />
                </div>
              )}
              <div className="form-group">
                <label>Send Delay</label>
                <div className="delay-inputs">
                  <input
                    type="number"
                    value={nodeConfig.config?.delay?.amount || 0}
                    onChange={(e) => updateConfig('delay', {
                      ...nodeConfig.config?.delay,
                      amount: parseInt(e.target.value) || 0
                    })}
                    className="form-input"
                    min="0"
                  />
                  <select
                    value={nodeConfig.config?.delay?.unit || 'hours'}
                    onChange={(e) => updateConfig('delay', {
                      ...nodeConfig.config?.delay,
                      unit: e.target.value
                    })}
                    className="form-select"
                  >
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {/* Delay Configuration */}
          {node.type === 'delay' && (
            <div className="form-group">
              <label>Wait Duration</label>
              <div className="delay-inputs">
                <input
                  type="number"
                  value={nodeConfig.config?.duration?.amount || 1}
                  onChange={(e) => updateConfig('duration', {
                    ...nodeConfig.config?.duration,
                    amount: parseInt(e.target.value) || 1
                  })}
                  className="form-input"
                  min="1"
                />
                <select
                  value={nodeConfig.config?.duration?.unit || 'days'}
                  onChange={(e) => updateConfig('duration', {
                    ...nodeConfig.config?.duration,
                    unit: e.target.value
                  })}
                  className="form-select"
                >
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                  <option value="weeks">Weeks</option>
                  <option value="months">Months</option>
                </select>
              </div>
            </div>
          )}

          {/* Condition Configuration */}
          {node.type === 'condition' && (
            <>
              <div className="form-group">
                <label>Condition Field</label>
                <select
                  value={nodeConfig.config?.field || ''}
                  onChange={(e) => updateConfig('field', e.target.value)}
                  className="form-select"
                >
                  <option value="">Select field...</option>
                  <option value="application_status">Application Status</option>
                  <option value="interview_score">Interview Score</option>
                  <option value="email_opened">Email Opened</option>
                  <option value="profile_completed">Profile Completed</option>
                  <option value="experience_years">Years of Experience</option>
                </select>
              </div>

              <div className="form-group">
                <label>Operator</label>
                <select
                  value={nodeConfig.config?.operator || ''}
                  onChange={(e) => updateConfig('operator', e.target.value)}
                  className="form-select"
                >
                  <option value="">Select operator...</option>
                  <option value="equals">Equals</option>
                  <option value="not_equals">Not Equals</option>
                  <option value="greater_than">Greater Than</option>
                  <option value="less_than">Less Than</option>
                  <option value="contains">Contains</option>
                </select>
              </div>

              <div className="form-group">
                <label>Value</label>
                <input
                  type="text"
                  value={nodeConfig.config?.value || ''}
                  onChange={(e) => updateConfig('value', e.target.value)}
                  placeholder="Enter comparison value"
                  className="form-input"
                />
              </div>
            </>
          )}

          {/* Action Configuration */}
          {node.type === 'action' && (
            <div className="form-group">
              <label>Action Type</label>
              <select
                value={nodeConfig.config?.actionType || ''}
                onChange={(e) => updateConfig('actionType', e.target.value)}
                className="form-select"
              >
                <option value="">Select action...</option>
                <option value="update_status">Update Application Status</option>
                <option value="schedule_interview">Schedule Interview</option>
                <option value="add_tag">Add Candidate Tag</option>
                <option value="create_task">Create Task</option>
                <option value="send_notification">Send Internal Notification</option>
              </select>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export default JourneyBuilderScreen;